/**
 * HEIC/HEIF support for the web image upload flow.
 *
 * iPhones shoot photos as HEIC by default, but Chrome/Firefox/Edge cannot decode
 * HEIC natively (only Safari can). Our upload pipeline decodes every image with the
 * browser's native decoder (react-image-file-resizer and blueimp-load-image both use
 * an <img>/canvas), so HEIC uploads silently fail/hang on non-Safari browsers.
 *
 * This converts HEIC -> JPEG on the client before those decoders run. Everything
 * downstream already standardizes to JPEG, so no backend changes are needed.
 *
 * The ~1-2MB libheif WASM in `heic-to` is loaded lazily (dynamic import) and only
 * after the cheap `looksLikeHeic` gate passes, so non-HEIC uploads pay no cost and
 * the WASM never ends up in the main bundle.
 */

const HEIC_MIME_TYPES = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence'];
const HEIC_EXTENSION = /\.(heic|heif)$/i;

/**
 * Cheap synchronous gate. Browsers often report an empty `file.type` for `.heic`
 * files, so we also fall back to the filename extension.
 */
export function looksLikeHeic(file: File): boolean {
  if (file.type && HEIC_MIME_TYPES.includes(file.type.toLowerCase())) {
    return true;
  }

  return HEIC_EXTENSION.test(file.name || '');
}

/**
 * Converts a HEIC/HEIF file to JPEG. Returns the original file unchanged when it is
 * not HEIC (or when running server-side), so it is safe to call on every upload.
 *
 * Throws if conversion itself fails — callers should use `convertHeicIfNeeded` to
 * get a non-throwing fallback.
 */
export async function convertHeicToJpeg(file: File): Promise<File> {
  // SSR guard: the WASM library is browser-only.
  if (typeof window === 'undefined') {
    return file;
  }

  // Cheap skip for non-HEIC files — avoids loading the WASM bundle.
  if (!looksLikeHeic(file)) {
    return file;
  }

  // Lazy-load the libheif WASM only when we actually have a HEIC candidate.
  const { heicTo, isHeic } = await import('heic-to');

  // Authoritative magic-byte check (guards against e.g. a JPEG misnamed `.heic`).
  if (!(await isHeic(file))) {
    return file;
  }

  // quality 0.9 because react-image-file-resizer re-encodes at 80 afterwards —
  // keep this pass high to avoid compounding quality loss.
  const jpegBlob = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.9 });

  const newName = (file.name || 'image').replace(HEIC_EXTENSION, '.jpg');

  return new File([jpegBlob], newName, { type: 'image/jpeg', lastModified: file.lastModified });
}

/**
 * Non-throwing wrapper around `convertHeicToJpeg`. On failure it reports via `onError`
 * and returns the original file, so a failed decode never hangs the upload (the
 * downstream decoder then fails exactly as it does today, i.e. no regression).
 */
export async function convertHeicIfNeeded(file: File, onError?: (error: unknown) => void): Promise<File> {
  try {
    return await convertHeicToJpeg(file);
  } catch (error) {
    onError?.(error);

    return file;
  }
}
